import { matchesByTag, selectByCategory, type FilterablePlace } from './placeFilter';
import { LISA_SEEDED_MOVEMENT_IDS } from './yogaSessions';

/**
 * Household Favorites (the tab) — Chip and Lisa, localStorage only.
 *
 * Hearts (Family Favorites, under More) stay in D1. Checks are per-profile on this phone.
 * No login. Uncheck removes. Lisa is pre-seeded with the four gentle
 * movement items; Power Yoga is not a default.
 */

export const INTERESTS_STORAGE_KEY = 'bn:interests:v2';
export const INTERESTS_STORAGE_KEY_V1 = 'bn:interests:v1';

export const HOUSEHOLD_PROFILES = [
  { id: 'chip', label: 'Chip' },
  { id: 'lisa', label: 'Lisa' },
] as const;

export type ProfileId = (typeof HOUSEHOLD_PROFILES)[number]['id'];

export type InterestKind = 'category' | 'club' | 'session';

export interface InterestDef {
  id: string;
  label: string;
  kind: InterestKind;
  /** Places browse category this interest maps to. */
  placeCategory?: string;
  /** Extra tag match, same rule as the Places Fitness chip. */
  placeTag?: string;
  clubId?: string;
}

export const INTEREST_CATALOG: InterestDef[] = [
  { id: 'fitness', label: 'Fitness', kind: 'category', placeCategory: 'fitness', placeTag: 'exercise' },
  { id: 'book-club', label: 'Book club', kind: 'club', clubId: 'take-a-break-book-club' },
  { id: 'grocery', label: 'Grocery', kind: 'category', placeCategory: 'grocery' },
  { id: 'food', label: 'Restaurants', kind: 'category', placeCategory: 'food' },
  { id: 'wga', label: 'WGA golf', kind: 'club', clubId: 'bnwga' },
  { id: 'recreation', label: 'Parks', kind: 'category', placeCategory: 'recreation' },
  { id: 'yin-yoga', label: 'Yin Yoga', kind: 'session' },
  { id: 'yoga-in-the-park', label: 'Yoga in the Park', kind: 'session' },
  { id: 'tai-chi', label: 'Tai Chi', kind: 'session' },
  { id: 'sound-bath', label: 'Sound Bath', kind: 'session' },
  { id: 'power-yoga', label: 'Power Yoga', kind: 'session' },
  { id: 'wellway-yoga', label: 'WellWay yoga', kind: 'session' },
];

export const LISA_SEEDED_INTEREST_IDS: string[] = [...LISA_SEEDED_MOVEMENT_IDS];

export interface ProfileInterests {
  interestIds: string[];
  placeSlugs: string[];
}

export interface InterestsV2 {
  v: 2;
  activeProfile: ProfileId;
  profiles: Record<ProfileId, ProfileInterests>;
}

/** @deprecated Use InterestsV2. Kept for v1 → v2 migration. */
export interface InterestsV1 {
  v: 1;
  displayName: string;
  interestIds: string[];
  placeSlugs: string[];
}

const KNOWN_IDS = new Set(INTEREST_CATALOG.map((i) => i.id));

function unique(items: string[]): string[] {
  return [...new Set(items)];
}

function sanitizeList(list: unknown, knownOnly = false): string[] {
  if (!Array.isArray(list)) return [];
  return unique(
    list.filter(
      (id): id is string =>
        typeof id === 'string' && id.length > 0 && (!knownOnly || KNOWN_IDS.has(id)),
    ),
  );
}

function sanitizeProfile(raw: unknown): ProfileInterests {
  const data = raw && typeof raw === 'object' ? (raw as Partial<ProfileInterests>) : {};
  return {
    interestIds: sanitizeList(data.interestIds, true),
    placeSlugs: sanitizeList(data.placeSlugs),
  };
}

function emptyProfile(): ProfileInterests {
  return { interestIds: [], placeSlugs: [] };
}

function lisaSeededProfile(): ProfileInterests {
  return { interestIds: [...LISA_SEEDED_INTEREST_IDS], placeSlugs: [] };
}

export function emptyInterests(): InterestsV2 {
  return {
    v: 2,
    activeProfile: 'chip',
    profiles: {
      chip: emptyProfile(),
      lisa: lisaSeededProfile(),
    },
  };
}

export function isProfileId(value: unknown): value is ProfileId {
  return value === 'chip' || value === 'lisa';
}

export function profileLabel(id: ProfileId): string {
  return HOUSEHOLD_PROFILES.find((p) => p.id === id)?.label ?? id;
}

function migrateV1(data: Partial<InterestsV1>): InterestsV2 {
  const displayName = typeof data.displayName === 'string' ? data.displayName : '';
  const dest: ProfileId = /lisa/i.test(displayName) ? 'lisa' : 'chip';
  const migrated: ProfileInterests = {
    interestIds: sanitizeList(data.interestIds, true),
    placeSlugs: sanitizeList(data.placeSlugs),
  };
  if (dest === 'lisa') {
    migrated.interestIds = unique([...migrated.interestIds, ...LISA_SEEDED_INTEREST_IDS]);
  }
  return {
    v: 2,
    activeProfile: dest,
    profiles: {
      chip: dest === 'chip' ? migrated : emptyProfile(),
      lisa: dest === 'lisa' ? migrated : lisaSeededProfile(),
    },
  };
}

function normalizeV2(data: Partial<InterestsV2>): InterestsV2 {
  const activeProfile = isProfileId(data.activeProfile) ? data.activeProfile : 'chip';
  const rawProfiles = data.profiles && typeof data.profiles === 'object' ? data.profiles : undefined;
  const chip = sanitizeProfile(rawProfiles?.chip);
  // Seed Lisa only when her profile was never written. An explicit empty
  // list means she unchecked everything — do not put the defaults back.
  const lisa =
    rawProfiles && Object.prototype.hasOwnProperty.call(rawProfiles, 'lisa')
      ? sanitizeProfile(rawProfiles.lisa)
      : lisaSeededProfile();
  return { v: 2, activeProfile, profiles: { chip, lisa } };
}

/**
 * Parse v2, or migrate a v1 blob. Junk / missing → Chip empty + Lisa seeded.
 * Pass the legacy v1 localStorage value as `legacyV1` when the v2 key is empty.
 */
export function parseInterests(raw: string | null | undefined, legacyV1?: string | null): InterestsV2 {
  if (raw) {
    try {
      const data = JSON.parse(raw) as Partial<InterestsV2> & Partial<InterestsV1>;
      if (data && data.v === 2) return normalizeV2(data);
      if (data && data.v === 1) return migrateV1(data);
    } catch {
      /* fall through */
    }
  }
  if (legacyV1) {
    try {
      const data = JSON.parse(legacyV1) as Partial<InterestsV1>;
      if (data && data.v === 1) return migrateV1(data);
    } catch {
      /* fall through */
    }
  }
  return emptyInterests();
}

export function serializeInterests(state: InterestsV2): string {
  const normalized = normalizeV2(state);
  return JSON.stringify({
    v: 2,
    activeProfile: normalized.activeProfile,
    profiles: {
      chip: {
        interestIds: normalized.profiles.chip.interestIds,
        placeSlugs: normalized.profiles.chip.placeSlugs,
      },
      lisa: {
        interestIds: normalized.profiles.lisa.interestIds,
        placeSlugs: normalized.profiles.lisa.placeSlugs,
      },
    },
  });
}

export function activeSlice(state: InterestsV2): ProfileInterests {
  return state.profiles[state.activeProfile];
}

export function toggleId(list: string[], id: string): string[] {
  return list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
}

export function interestById(id: string): InterestDef | undefined {
  return INTEREST_CATALOG.find((i) => i.id === id);
}

export const CATEGORY_INTERESTS = INTEREST_CATALOG.filter((i) => i.kind !== 'session');
export const SESSION_INTERESTS = INTEREST_CATALOG.filter((i) => i.kind === 'session');

/**
 * A place matches a category interest the same way Places chips do —
 * Fitness includes `exercise`-tagged parks/shelters, not just category
 * `fitness`. Club and session interests do not match places.
 */
export function placeMatchesInterest(place: FilterablePlace, interest: InterestDef): boolean {
  if (interest.kind !== 'category' || !interest.placeCategory) return false;
  if (place.category === interest.placeCategory) return true;
  if (interest.placeTag && place.tags.includes(interest.placeTag)) return true;
  if (interest.placeCategory === 'fitness') return matchesByTag('fitness', place);
  return false;
}

export function placesForInterest<T extends FilterablePlace>(places: T[], interest: InterestDef): T[] {
  if (interest.kind !== 'category' || !interest.placeCategory) return [];
  return selectByCategory(places, interest.placeCategory);
}

export function pinnedPlaces<T extends FilterablePlace & { slug: string }>(
  places: T[],
  slugs: string[],
): T[] {
  const want = new Set(slugs);
  return places.filter((p) => want.has(p.slug));
}
